import { $4, $$4, formatMoney } from 'global';

class TymHeaderCart extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.threshold = parseInt(this.getAttribute('threshold'), 10) || 0;
    this.totalTargets = $$4('[data-tym-cart-total]', this);
    this.shipTargets = $$4('[data-tym-ship]', this);

    document.addEventListener(
      'cart:change',
      (event) => {
        if (event.detail && event.detail.cart) this.render(event.detail.cart);
      },
      { signal }
    );
    document.addEventListener('cart:refresh', () => this.pull(), { signal });
    window.addEventListener('pageshow', () => this.pull(), { signal });
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
  }

  async pull() {
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart.js`);
      if (!response.ok) return;
      this.render(await response.json());
    } catch (error) {
      return;
    }
  }

  money(cents) {
    return formatMoney(cents, window.themeHDN.settings.currencyFormat);
  }

  render(cart) {
    const total = typeof cart.total_price === 'number' ? cart.total_price : 0;

    this.totalTargets.forEach((node) => {
      node.innerHTML = `<span class="hdt-money">${this.money(total)}</span>`;
    });

    if (this.threshold > 0) {
      const remaining = Math.max(this.threshold - total, 0);
      const ratio = Math.min(total / this.threshold, 1);

      this.shipTargets.forEach((node) => {
        const label = $4('[data-tym-ship-label]', node);
        const fill = $4('[data-tym-ship-fill]', node);

        if (fill) fill.style.width = `${Math.round(ratio * 100)}%`;

        if (!label) return;
        if (remaining > 0) {
          const template = label.getAttribute('data-tym-ship-label') || '';
          label.innerHTML = template.replace(
            '[amount]',
            `<span class="hdt-money">${this.money(remaining)}</span>`
          );
        } else {
          label.textContent = label.getAttribute('data-tym-ship-done') || '';
        }
      });
    }

    this.dispatchEvent(new CustomEvent('products:update', { bubbles: true }));
  }
}

if (!customElements.get('tym-header-cart')) {
  customElements.define('tym-header-cart', TymHeaderCart);
}

class TymChipRail extends HTMLElement {
  connectedCallback() {
    this.abortController = new AbortController();
    const active = $4('[aria-current="page"], .is-active', this);
    if (!active) return;
    const offset = active.offsetLeft - (this.clientWidth - active.clientWidth) / 2;
    this.scrollTo({ left: Math.max(offset, 0), behavior: 'auto' });
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
  }
}

if (!customElements.get('tym-chip-rail')) {
  customElements.define('tym-chip-rail', TymChipRail);
}

class TymNavPill extends HTMLElement {
  connectedCallback() {
    this.nav = this.parentElement;
    this.list = $4('.tym-hdr__nav-list', this.nav);
    this.base = this.closest('.tym-hdr__bar');
    if (!this.list || !this.base) return;

    this.links = $$4('.tym-hdr__nav-link', this.list);
    if (!this.links.length) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.anchor =
      $4('.tym-hdr__nav-item.is-current .tym-hdr__nav-link', this.list) ||
      $4('.tym-hdr__nav-link[aria-current="page"]', this.list);

    this.links.forEach((link) => {
      link.addEventListener('pointerenter', () => this.moveTo(link), { signal });
      link.addEventListener('focusin', () => this.moveTo(link), { signal });
      link.addEventListener(
        'click',
        () => {
          this.anchor = link;
          this.moveTo(link);
        },
        { signal }
      );
    });

    this.list.addEventListener('pointerleave', () => this.moveTo(this.resting()), { signal });
    this.list.addEventListener('focusout', (event) => {
      if (!this.list.contains(event.relatedTarget)) this.moveTo(this.resting());
    }, { signal });

    $$4('details', this.list).forEach((details) => {
      details.addEventListener('toggle', () => this.moveTo(this.resting()), { signal });
    });

    if (window.ResizeObserver) {
      this.observer = new ResizeObserver(() => this.moveTo(this.resting(), false));
      this.observer.observe(this.list);
    }

    this.moveTo(this.anchor, false);
    this.nav.classList.add('is-pill-ready');
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    if (this.observer) this.observer.disconnect();
  }

  resting() {
    const open = $4('details[open] > .tym-hdr__nav-link', this.list);
    return open || this.anchor;
  }

  moveTo(target, animate = true) {
    if (!target) {
      this.style.opacity = '0';
      this.links.forEach((link) => link.classList.remove('is-lit'));
      return;
    }

    const baseRect = this.base.getBoundingClientRect();
    const rect = target.getBoundingClientRect();
    if (!rect.width || !baseRect.width) return;

    if (!animate) this.style.transition = 'none';

    this.style.width = `${rect.width}px`;
    this.style.height = `${rect.height}px`;
    this.style.transform = `translate3d(${rect.left - baseRect.left - this.base.clientLeft}px, ${
      rect.top - baseRect.top - this.base.clientTop
    }px, 0)`;
    this.style.removeProperty('opacity');

    if (!animate) {
      this.offsetHeight;
      this.style.removeProperty('transition');
    }

    this.links.forEach((link) => link.classList.toggle('is-lit', link === target));
  }
}

class TymShopSwitch extends HTMLElement {
  connectedCallback() {
    this.cats = $$4('.tym-hdr__cat', this);
    this.views = $$4('.tym-hdr__shop-view', this);
    if (!this.cats.length || !this.views.length) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.first = this.cats[0].dataset.shopCat;
    this.current = this.first;

    this.cats.forEach((cat) => {
      cat.addEventListener('pointerenter', () => this.show(cat.dataset.shopCat), { signal });
      cat.addEventListener('focus', () => this.show(cat.dataset.shopCat), { signal });
    });

    const panel = this.closest('details');
    if (panel) {
      panel.addEventListener('toggle', () => {
        if (panel.open) this.show(this.first, true);
      }, { signal });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  show(index, force) {
    if (index == null) return;
    if (index === this.current && !force) return;
    this.current = index;
    this.cats.forEach((cat) => {
      if (cat.dataset.shopCat === index) cat.setAttribute('data-active', 'true');
      else cat.removeAttribute('data-active');
    });
    this.views.forEach((view) => {
      const active = view.dataset.shopView === index;
      view.hidden = !active;
      view.classList.remove('is-in');
      if (active) {
        void view.offsetWidth;
        view.classList.add('is-in');
      }
    });
  }
}

if (!customElements.get('tym-shop-switch')) {
  customElements.define('tym-shop-switch', TymShopSwitch);
}

if (!customElements.get('tym-nav-pill')) {
  customElements.define('tym-nav-pill', TymNavPill);
}

class TymRotator extends HTMLElement {
  connectedCallback() {
    this.frames = $$4('.tym-hdr__rotator-frame', this);
    if (this.frames.length < 2) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    const seconds = parseFloat(this.getAttribute('interval')) || 7;
    this.interval = Math.max(seconds, 2) * 1000;
    this.still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.index = Math.max(
      this.frames.findIndex((frame) => frame.classList.contains('is-on')),
      0
    );
    this.frames[this.index].classList.add('is-on');
    if (!this.shown(this.frames[this.index])) this.advance();

    if (window.Shopify && window.Shopify.designMode) {
      document.addEventListener(
        'shopify:block:select',
        ({ target }) => {
          if (!this.contains(target)) return;
          this.stop();
          this.show(this.frames.indexOf(target));
        },
        { signal }
      );

      document.addEventListener(
        'shopify:block:deselect',
        ({ target }) => {
          if (this.contains(target)) this.start();
        },
        { signal }
      );
    }

    document.addEventListener(
      'visibilitychange',
      () => (document.hidden ? this.stop() : this.start()),
      { signal }
    );

    this.start();
  }

  disconnectedCallback() {
    this.stop();
    if (this.abortController) this.abortController.abort();
  }

  start() {
    this.stop();
    if (this.still) return;
    this.timer = setInterval(() => this.advance(), this.interval);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  shown(frame) {
    return getComputedStyle(frame).display !== 'none';
  }

  show(next) {
    if (next < 0 || next === this.index) return;
    this.frames[this.index].classList.remove('is-on');
    this.index = next;
    this.frames[this.index].classList.add('is-on');
  }

  advance() {
    let next = this.index;
    for (let step = 0; step < this.frames.length; step += 1) {
      next = (next + 1) % this.frames.length;
      if (this.shown(this.frames[next])) break;
    }
    this.show(next);
  }
}

if (!customElements.get('tym-rotator')) {
  customElements.define('tym-rotator', TymRotator);
}
